import React, { useState, useEffect } from 'react';
import VerificationBlock from './VerificationBlock';
import PhoneVerificationBlock from './PhoneVerificationBlock';
import SignaturePad from './SignaturePad';
import { mockApi } from '../api/mockApi';
import { phoneOtpApi } from '../api/phoneOtpApi';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import { FiCheckCircle, FiArrowRight, FiLock, FiMail, FiClock, FiFileText, FiCalendar, FiMapPin, FiShield } from 'react-icons/fi';

const SUBMISSION_STAGES = [
  "Validating verified session...",
  "Submitting your enquiry...",
  "Generating lead notification...",
  "Sending your acknowledgement...",
  "Finalising confirmation..."
];

const DynamicForm = ({ formSchema, categoryId }) => {
  const [formData, setFormData] = useState({});
  const [verification, setVerification] = useState({
    isVerified: false,
    verificationId: null,
    sessionToken: null,
    email: '',
    phone: '',
    verifiedEmail: '',
    verifiedAt: null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStage, setSubmitStage] = useState(0);
  const [successData, setSuccessData] = useState(null);

  useEffect(() => {
    let interval;
    if (isSubmitting) {
      interval = setInterval(() => {
        setSubmitStage(prev => (prev < SUBMISSION_STAGES.length - 1 ? prev + 1 : prev));
      }, 1000);
    } else {
      setSubmitStage(0);
    }
    return () => clearInterval(interval);
  }, [isSubmitting]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleCustomChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isFullyVerified = verification.isVerified;
  const isWaiver = categoryId === 'waivers';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFullyVerified) return;

    // Check for required signatures or checkboxes that might not be handled by HTML required
    const missingFields = formSchema.fields.filter(f => {
      if (!f.required) return false;
      const val = formData[f.name];
      if (f.type === 'signature') return !val;
      if (f.type === 'checkbox') return !val;
      return false;
    });

    if (missingFields.length > 0) {
      alert(`Please complete all required fields: ${missingFields.map(f => f.label).join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      const isPhone = formSchema.verificationType === 'phone';
      const api = isPhone ? phoneOtpApi : mockApi;
      
      const payload = {
        ...formData,
        formType: formSchema.id,
        category: categoryId,
        ...(isPhone 
          ? { phone: verification.phone } 
          : { email: verification.verifiedEmail || verification.email, verificationId: verification.verificationId, sessionToken: verification.sessionToken }
        ),
        verifiedAt: verification.verifiedAt,
        isWaiverSubmission: isWaiver
      };

      const result = await api.submitForm(payload);
      if (result.ok) setSuccessData(result);
    } catch (error) {
      alert("Submission failed: " + error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (successData) {
    return (
      <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-2xl shadow-xl p-8 md:p-12 text-center border border-slate-100 max-w-2xl mx-auto">
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <SafeIcon icon={FiCheckCircle} className="text-green-600 text-3xl" />
        </div>
        
        <h2 className="text-2xl font-extrabold text-slate-900 mb-4">
          {isWaiver ? 'Signed Waiver Received' : 'Submission Received'}
        </h2>
        
        <div className="text-slate-600 text-left space-y-5 mb-10">
          <p className="text-center font-medium">
            {isWaiver 
              ? 'Thank you. Your verified waiver and consent record has been received and timestamped.' 
              : 'Thank you for your enquiry. Your verified submission has been successfully received.'}
          </p>
          
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
            <p className="text-xs font-bold text-slate-900 mb-3 flex items-center gap-2 uppercase tracking-wider">
              <SafeIcon icon={isWaiver ? FiFileText : FiMail} className="text-blue-600" /> 
              Automated Workflow Triggered:
            </p>
            <ul className="space-y-2 text-sm">
              {isWaiver ? (
                <>
                  <li className="flex items-start gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 shrink-0" /> <span>Signed waiver record created</span></li>
                  <li className="flex items-start gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 shrink-0" /> <span>Timestamp & IP captured</span></li>
                  <li className="flex items-start gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 shrink-0" /> <span>Verification details attached</span></li>
                  <li className="flex items-start gap-2"><SafeIcon icon={FiCheckCircle} className="text-green-500 mt-1 shrink-0" /> <span>Guardian/Customer copy sent</span></li>
                </>
              ) : (
                <>
                  <li className="flex items-start gap-2"><span>•</span> <span>Lead notification sent to team</span></li>
                  <li className="flex items-start gap-2"><span>•</span> <span>Customer acknowledgement generated</span></li>
                </>
              )}
              {successData.submissionId && (
                <li className="flex items-start gap-2 font-mono text-[10px] text-slate-400 mt-4 bg-white p-2 rounded border border-slate-100">
                  RECORD_ID: {successData.submissionId}
                </li>
              )}
            </ul>
          </div>
        </div>
        
        <button onClick={() => { setSuccessData(null); setFormData({}); }} className="bg-slate-900 hover:bg-slate-800 text-white px-10 py-3.5 rounded-lg font-bold transition-all shadow-lg active:scale-95">
          Test Another Form
        </button>
      </motion.div>
    );
  }

  const VBlock = formSchema.verificationType === 'phone' ? PhoneVerificationBlock : VerificationBlock;

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden border border-slate-100">
      <div className="grid md:grid-cols-3">
        <div className="bg-slate-900 text-white p-8 md:p-10 flex flex-col justify-between hidden md:flex">
          <div>
            <span className="inline-block px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-[10px] font-bold uppercase tracking-widest mb-6">
              {formSchema.verificationType === 'phone' ? 'SMS Verified' : 'Email Verified'}
            </span>
            <h2 className="text-3xl font-extrabold mb-4 leading-tight">{formSchema.title}</h2>
            <p className="text-slate-400 text-sm mb-8 leading-relaxed font-medium">{formSchema.description}</p>
            
            {isWaiver && (
              <div className="p-4 bg-blue-500/5 border border-blue-500/10 rounded-xl text-xs text-blue-300 italic leading-relaxed space-y-3">
                <div className="flex gap-2">
                  <SafeIcon icon={FiShield} className="shrink-0 mt-0.5" />
                  <p>This demo captures a verified digital signature and generates a timestamped record.</p>
                </div>
                <div className="flex gap-2 text-[10px] opacity-70">
                  <SafeIcon icon={FiClock} className="shrink-0 mt-0.5" />
                  <p>Records are logged in real-time to internal systems.</p>
                </div>
              </div>
            )}
          </div>
          <div className="text-[10px] text-slate-500 text-center font-bold tracking-widest uppercase">Verified by BPA Solutions</div>
        </div>

        <div className="md:col-span-2 p-6 md:p-10">
          <form onSubmit={handleSubmit} className="space-y-6">
            <VBlock formType={formSchema.id} categoryId={categoryId} onVerifyStatusChange={setVerification} />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {formSchema.fields.map((field, idx) => {
                const name = field.name || `static_${idx}`;
                
                if (field.type === 'sectionHeading') {
                  return (
                    <div key={name} className="col-span-full pt-4 pb-1 border-b border-slate-100">
                      <h4 className="text-xs font-black text-slate-900 uppercase tracking-widest">{field.label}</h4>
                    </div>
                  );
                }

                if (field.type === 'staticInfo') {
                  return (
                    <div key={name} className="col-span-full bg-slate-50 p-4 rounded-xl border border-slate-200 text-xs text-slate-600 leading-relaxed italic">
                      {field.content}
                    </div>
                  );
                }

                if (field.type === 'signature') {
                  return (
                    <SignaturePad 
                      key={name}
                      label={field.label}
                      required={field.required}
                      value={formData[field.name] || ''}
                      onChange={(val) => handleCustomChange(field.name, val)}
                    />
                  );
                }

                return (
                  <div key={name} className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">
                      {field.label} {field.required && <span className="text-red-500">*</span>}
                    </label>
                    
                    {field.type === 'select' ? (
                      <select name={field.name} required={field.required} value={formData[field.name] || ''} onChange={handleInputChange} className="w-full rounded-lg border-slate-300 border p-2.5 bg-white focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium">
                        <option value="">Select...</option>
                        {field.options?.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                    ) : field.type === 'textarea' ? (
                      <textarea name={field.name} required={field.required} value={formData[field.name] || ''} onChange={handleInputChange} rows={3} className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all resize-none font-medium" />
                    ) : field.type === 'checkbox' ? (
                      <label className="flex items-center gap-3 cursor-pointer group">
                        <input type="checkbox" name={field.name} required={field.required} checked={!!formData[field.name]} onChange={handleInputChange} className="w-5 h-5 rounded border-slate-300 text-blue-600 focus:ring-blue-500" />
                        <span className="text-sm font-bold text-slate-700 group-hover:text-slate-900 transition-colors">{field.label}</span>
                      </label>
                    ) : field.type === 'radio' ? (
                      <div className="flex gap-6">
                        {field.options.map(opt => (
                          <label key={opt} className="flex items-center gap-2 cursor-pointer group">
                            <input type="radio" name={field.name} value={opt} required={field.required} checked={formData[field.name] === opt} onChange={handleInputChange} className="w-4 h-4 text-blue-600 focus:ring-blue-500" />
                            <span className="text-sm font-bold text-slate-700 group-hover:text-slate-900">{opt}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <input type={field.type} name={field.name} required={field.required} value={formData[field.name] || ''} onChange={handleInputChange} className="w-full rounded-lg border-slate-300 border p-2.5 focus:ring-2 focus:ring-blue-500 outline-none transition-all font-medium" />
                    )}
                  </div>
                );
              })}
            </div>

            {isWaiver && (
              <div className="bg-amber-50 border border-amber-100 p-3 rounded-lg text-[10px] text-amber-700 font-medium">
                <strong>Disclaimer:</strong> This demo captures signed acknowledgments and consent records. Businesses should review their waiver wording with their own legal adviser before use.
              </div>
            )}

            <div className="mt-6 pt-6 border-t border-slate-100 flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest">
                  {isFullyVerified ? (
                    <span className="text-green-600 flex items-center gap-1.5"><SafeIcon icon={FiCheckCircle} /> Ready to {isWaiver ? 'Sign & Submit' : 'Submit'}</span>
                  ) : (
                    <span className="text-slate-400 flex items-center gap-1.5"><SafeIcon icon={FiLock} /> Verification required</span>
                  )}
                </div>
                <button type="submit" disabled={!isFullyVerified || isSubmitting} className="bg-blue-600 hover:bg-blue-700 text-white px-10 py-3.5 rounded-lg font-extrabold transition-all disabled:opacity-30 disabled:grayscale flex items-center gap-2 shadow-lg active:scale-95 text-sm uppercase tracking-wider">
                  {isSubmitting ? 'Processing...' : (isWaiver ? 'Submit Signed Waiver' : 'Submit Verified Enquiry')}
                  {!isSubmitting && <SafeIcon icon={FiArrowRight} />}
                </button>
              </div>
              <AnimatePresence>
                {isSubmitting && (
                  <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="flex items-center justify-end gap-2 text-blue-600 text-xs font-bold pr-1">
                    <SafeIcon icon={FiClock} className="animate-spin" />
                    <span>{SUBMISSION_STAGES[submitStage]}</span>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </form>

          {isWaiver && (
            <div className="mt-8 p-6 bg-slate-50 rounded-2xl border border-slate-200 text-center">
              <p className="text-xs font-bold text-slate-900 mb-3">Need a custom waiver workflow for your business?</p>
              <a href="https://tidycal.com/anishmmalhotra/initial-20-minutes-discussion" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-blue-600 text-xs font-black uppercase tracking-widest hover:text-blue-700 transition-colors">
                Book a Custom Form Demo <SafeIcon icon={FiArrowRight} />
              </a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DynamicForm;